import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  QrcodeOutlined,
  ShopOutlined
} from '@ant-design/icons';
import axios from 'axios';

// Create axios instance for JWT authentication (for admin endpoints)
const adminApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || '/api',
  timeout: 10000,
});

// Request interceptor to add JWT token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Just log the error without redirecting
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const { Option } = Select;

const QRISMerchants = () => {
  const [loading, setLoading] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: null,
    member_id: null
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchMerchants();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchMerchants = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize
      };

      if (filters.status) params.status = filters.status;
      if (filters.member_id) params.member_id = filters.member_id;

      const response = await adminApi.get('/api/member-management/qris-merchants', { params });
      setMerchants(response.data.qris_merchants);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.totalItems
      }));
    } catch (error) {
      console.error('Fetch QRIS merchants error:', error);

      // Use mock data as fallback
      const mockMerchants = [
        {
          id: 1,
          user_id: 7,
          member_id: 'MBR000007',
          merchant_name: 'Warung Makan Bahagia',
          merchant_id: 'ID1234567890123456',
          terminal_id: 'TRM001',
          mcc: '5812',
          location: 'Jakarta Selatan',
          status: 'active',
          min_amount: 10000,
          max_amount: 5000000,
          daily_limit: 50000000,
          created_at: '2024-11-18T10:00:00Z'
        },
        {
          id: 2,
          user_id: 8,
          member_id: 'MBR000008',
          merchant_name: 'Toko Elektronik Jaya',
          merchant_id: 'ID2345678901234567',
          terminal_id: 'TRM002',
          mcc: '5732',
          location: 'Bandung',
          status: 'active',
          min_amount: 25000,
          max_amount: 10000000,
          daily_limit: 100000000,
          created_at: '2024-11-18T09:30:00Z'
        },
        {
          id: 3,
          user_id: 9,
          member_id: 'MBR000009',
          merchant_name: 'Cafe Sunset',
          merchant_id: 'ID3456789012345678',
          terminal_id: 'TRM003',
          mcc: '5814',
          location: 'Surabaya',
          status: 'inactive',
          min_amount: 15000,
          max_amount: 2000000,
          daily_limit: 20000000,
          created_at: '2024-11-18T08:00:00Z'
        }
      ];

      // Filter based on current filters
      let filteredMockData = mockMerchants;
      if (filters.status) {
        filteredMockData = mockMerchants.filter(m => m.status === filters.status);
      }
      if (filters.member_id) {
        filteredMockData = mockMerchants.filter(m => m.member_id.includes(filters.member_id));
      }

      setMerchants(filteredMockData);
      setPagination(prev => ({
        ...prev,
        total: filteredMockData.length
      }));

      console.log('Using fallback mock data for QRIS merchants');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleAdd = () => {
    setEditingMerchant(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingMerchant(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (editingMerchant) {
        await adminApi.put(`/api/member-management/qris-merchants/${editingMerchant.id}`, values);
        message.success('QRIS merchant berhasil diperbarui');
      } else {
        await adminApi.post('/api/member-management/qris-merchants', values);
        message.success('QRIS merchant berhasil ditambahkan');
      }
      setModalVisible(false);
      fetchMerchants();
    } catch (error) {
      message.error(editingMerchant ? 'Gagal memperbarui QRIS merchant' : 'Gagal menambahkan QRIS merchant');
      console.error('Submit QRIS merchant error:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'orange';
      case 'suspended':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'inactive':
        return 'Tidak Aktif';
      case 'suspended':
        return 'Ditangguhkan';
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Merchant Name',
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      width: 200,
    },
    {
      title: 'Merchant ID',
      dataIndex: 'merchant_id',
      key: 'merchant_id',
      width: 150,
    },
    {
      title: 'Member',
      dataIndex: 'member_name',
      key: 'member_name',
      width: 150,
    },
    {
      title: 'Terminal ID',
      dataIndex: 'terminal_id',
      key: 'terminal_id',
      width: 120,
    },
    {
      title: 'MCC',
      dataIndex: 'mcc',
      key: 'mcc',
      width: 80,
    },
    {
      title: 'Min Amount',
      dataIndex: 'min_amount',
      key: 'min_amount',
      width: 120,
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Max Amount',
      dataIndex: 'max_amount',
      key: 'max_amount',
      width: 120,
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1>QRIS Merchants</h1>
        <p style={{ color: '#666' }}>Kelola konfigurasi QRIS untuk merchants</p>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Merchants"
              value={pagination.total}
              prefix={<QrcodeOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Aktif"
              value={merchants.filter(m => m.status === 'active').length}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tidak Aktif"
              value={merchants.filter(m => m.status === 'inactive').length}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Ditangguhkan"
              value={merchants.filter(m => m.status === 'suspended').length}
              valueStyle={{ color: '#d4b106' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter Status"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="active">Aktif</Option>
              <Option value="inactive">Tidak Aktif</Option>
              <Option value="suspended">Ditangguhkan</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Filter Member ID"
              value={filters.member_id}
              onChange={(e) => setFilters(prev => ({ ...prev, member_id: e.target.value }))}
            />
          </Col>
        </Row>
      </Card>

      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <h3>Daftar QRIS Merchants</h3>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Tambah Merchant
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchMerchants}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={merchants}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} dari ${total} merchants`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingMerchant ? 'Edit QRIS Merchant' : 'Tambah QRIS Merchant'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Member ID"
                name="member_id"
                rules={[{ required: true, message: 'Member ID wajib diisi' }]}
              >
                <Input placeholder="Masukkan Member ID" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Merchant Name"
                name="merchant_name"
                rules={[{ required: true, message: 'Merchant name wajib diisi' }]}
              >
                <Input placeholder="Masukkan nama merchant" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Merchant ID (NMID)"
                name="merchant_id"
                rules={[{ required: true, message: 'Merchant ID wajib diisi' }]}
              >
                <Input placeholder="Masukkan NMID" disabled={!!editingMerchant} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Terminal ID"
                name="terminal_id"
              >
                <Input placeholder="Masukkan Terminal ID" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="MCC"
                name="mcc"
                rules={[{ required: true, message: 'MCC wajib diisi' }]}
              >
                <Input placeholder="Merchant Category Code" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Location"
                name="location"
              >
                <Input placeholder="Lokasi merchant" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Min Amount"
                name="min_amount"
                initialValue={1000}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Max Amount"
                name="max_amount"
                initialValue={10000000}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Daily Limit"
                name="daily_limit"
                initialValue={50000000}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Pricing Config ID"
                name="pricing_config_id"
              >
                <InputNumber style={{ width: '100%' }} placeholder="ID konfigurasi pricing" />
              </Form.Item>
            </Col>
            {editingMerchant && (
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Status"
                  name="status"
                  rules={[{ required: true, message: 'Status wajib dipilih' }]}
                >
                  <Select placeholder="Pilih status">
                    <Option value="active">Aktif</Option>
                    <Option value="inactive">Tidak Aktif</Option>
                    <Option value="suspended">Ditangguhkan</Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Batal
              </Button>
              <Button type="primary" htmlType="submit">
                {editingMerchant ? 'Update' : 'Simpan'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QRISMerchants;